import {
  buildCollection,
  buildEntityCallbacks,
  buildProperties,
  buildProperty,
  EntityReference,
  type DataSource,
  type EntityCollection,
  type ResolvedEntityCollection,
} from "@firecms/core";
import { buildLocalizedStringProperty, type LocalizedString } from "../localization";
import {
  canAdminAccount,
  canAdminEvent,
  getAccountPath,
  getEventPath,
  type AccountCollectionId,
  type EventCollectionId,
} from "../utils";
import {
  onPreSaveContentTimestamps,
  contentTimestampsProperties,
  type ContentTimestamps,
} from "./contentTimestamps";
import { ImportNotificationDefsButton } from "../actions/ImportNotificationDefsButton";
import { SendGeneralNotificationButton } from "../actions/SendGeneralNotificationButton";
import type { AuthExtras } from "../types/auth";
import { NAV_GROUP_CONTENT_SESSIONS, NAV_GROUP_SESSION } from "./navigationGroups";

export const NotificationType = {
  CUSTOM: "custom",
  UNIT_UNLOCKED: "unit_unlocked",
  TOP_3: "top_3",
  RANKING_INDIVIDUAL_ENDED: "ranking_individual_ended",
  RANKING_TEAM_ENDED: "ranking_team_ended",
  PERSONAL_UNIT_COMPLETED: "personal_unit_completed",
  PERSONAL_TREASURE_OPENED: "personal_treasure_opened",
  PERSONAL_LEVELS_100: "personal_levels_100",
  TEAM_MEMBER_JOINED: "team_member_joined",
  TEAM_MEMBER_LEFT: "team_member_left",
  TEAM_MEMBER_UNIT_COMPLETED: "team_member_unit_completed",
  TEAM_MULTI_LEADER_PENALTY: "team_multi_leader_penalty",
  TEAM_MEMBER_PERSONAL_100: "team_member_personal_100",
  TEAM_LEVELS_100: "team_levels_100",
} as const;

export type NotificationType =
  (typeof NotificationType)[keyof typeof NotificationType];

/** Types an event admin may pick when sending a general notification. */
export const SENDABLE_GENERAL_NOTIFICATION_TYPES = [
  NotificationType.CUSTOM,
  NotificationType.TOP_3,
  NotificationType.RANKING_INDIVIDUAL_ENDED,
  NotificationType.RANKING_TEAM_ENDED,
] as const;

export type SendableGeneralNotificationType =
  (typeof SENDABLE_GENERAL_NOTIFICATION_TYPES)[number];

export const isSendableGeneralNotificationType = (
  value: unknown
): value is SendableGeneralNotificationType =>
  typeof value === "string" &&
  (SENDABLE_GENERAL_NOTIFICATION_TYPES as readonly string[]).includes(value);

export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  [NotificationType.CUSTOM]: "Custom (general)",
  [NotificationType.UNIT_UNLOCKED]: "Unit unlocked (automatic)",
  [NotificationType.TOP_3]: "Current top 3",
  [NotificationType.RANKING_INDIVIDUAL_ENDED]: "Individual ranking ended",
  [NotificationType.RANKING_TEAM_ENDED]: "Team ranking ended",
  [NotificationType.PERSONAL_UNIT_COMPLETED]: "Personal: unit completed",
  [NotificationType.PERSONAL_TREASURE_OPENED]: "Personal: treasure opened",
  [NotificationType.PERSONAL_LEVELS_100]: "Personal: levels 100%",
  [NotificationType.TEAM_MEMBER_JOINED]: "Team: member joined",
  [NotificationType.TEAM_MEMBER_LEFT]: "Team: member left",
  [NotificationType.TEAM_MEMBER_UNIT_COMPLETED]: "Team: member unit completed",
  [NotificationType.TEAM_MULTI_LEADER_PENALTY]: "Team: multi-leader penalty",
  [NotificationType.TEAM_MEMBER_PERSONAL_100]: "Team: member personal 100%",
  [NotificationType.TEAM_LEVELS_100]: "Team: levels 100%",
};

export type NotificationDef = ContentTimestamps & {
  type: NotificationType;
  title: LocalizedString;
  body: LocalizedString;
};

export type EventNotification = {
  type: string;
  title: LocalizedString;
  body: LocalizedString;
  sentAt: Date;
  fcmSentAt?: Date;
  payload?: Record<string, unknown>;
};

export type ScopedNotification = {
  type: string;
  title: LocalizedString;
  body: LocalizedString;
  actionLogRef?: EntityReference;
  actionAt?: Date;
  createdAt?: Date;
  payload?: Record<string, unknown>;
};

export const buildAccountNotificationDefsPath = (id: AccountCollectionId) =>
  `${getAccountPath(id)}/notificationDefs`;

export const buildEventNotificationsPath = (id: EventCollectionId) =>
  `${getEventPath(id)}/notifications`;

const notificationTypeProperty = buildProperty<string>({
  name: "Type",
  validation: { required: true },
  dataType: "string",
  enumValues: NOTIFICATION_TYPE_LABELS,
});

const assertSingleType = async ({
  values,
  entityId,
  path,
  collection,
  dataSource,
}: {
  values: Partial<NotificationDef>;
  entityId?: string;
  path: string;
  collection: EntityCollection<NotificationDef> | ResolvedEntityCollection<NotificationDef>;
  dataSource: DataSource;
}) => {
  if (!values.type) {
    return;
  }
  const entities = await dataSource.fetchCollection({
    path,
    collection,
  });
  const duplicate = entities.some(
    (entity) =>
      entity.id !== entityId && entity.values?.type === values.type
  );
  if (duplicate) {
    throw new Error(
      `Only one notification template of type "${values.type}" is allowed`
    );
  }
};

export const buildAccountNotificationDefsCollection = (
  id: AccountCollectionId
) =>
  buildCollection<NotificationDef>({
    name: "Notification templates",
    singularName: "Notification template",
    description:
      "Texts participants see when something happens in a session: unlocks, rankings, personal progress, and team activity. Edit them here for the whole account; send general ones from each event.",
    path: buildAccountNotificationDefsPath(id),
    id: "notificationDefs",
    group: NAV_GROUP_CONTENT_SESSIONS,
    icon: "Notifications",
    initialSort: ["updatedAt", "desc"],
    callbacks: buildEntityCallbacks<NotificationDef>({
      onPreSave: async ({
        values,
        status,
        entityId,
        path,
        collection,
        context,
      }) => {
        await assertSingleType({
          values,
          entityId,
          path,
          collection,
          dataSource: context.dataSource,
        });
        return onPreSaveContentTimestamps({ values, status });
      },
    }),
    permissions: ({ authController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const allowed = canAdminAccount(id, extra);
      return {
        read: allowed,
        edit: allowed,
        create: allowed,
        delete: allowed,
      };
    },
    Actions: ({ collection }) => [
      <ImportNotificationDefsButton
        key="import-notification-defs-json"
        collection={collection as EntityCollection<NotificationDef>}
      />,
    ],
    properties: buildProperties<NotificationDef>({
      type: notificationTypeProperty,
      title: buildLocalizedStringProperty({
        name: "Title",
        validation: { required: true },
        dataType: "string",
        description:
          "Supports placeholders like {{displayName}}, {{score}}, {{top1}}, {{unitTitle}}.",
      }),
      body: buildLocalizedStringProperty({
        name: "Body",
        validation: { required: true },
        multiline: true,
        dataType: "string",
        description:
          "Supports placeholders like {{displayName}}, {{score}}, {{top1}}, {{unitTitle}}.",
      }),
      ...contentTimestampsProperties,
    }),
  });

const generalNotificationTypeEnum = {
  [NotificationType.CUSTOM]: NOTIFICATION_TYPE_LABELS[NotificationType.CUSTOM],
  [NotificationType.UNIT_UNLOCKED]:
    NOTIFICATION_TYPE_LABELS[NotificationType.UNIT_UNLOCKED],
  [NotificationType.TOP_3]: NOTIFICATION_TYPE_LABELS[NotificationType.TOP_3],
  [NotificationType.RANKING_INDIVIDUAL_ENDED]:
    NOTIFICATION_TYPE_LABELS[NotificationType.RANKING_INDIVIDUAL_ENDED],
  [NotificationType.RANKING_TEAM_ENDED]:
    NOTIFICATION_TYPE_LABELS[NotificationType.RANKING_TEAM_ENDED],
};

export const buildEventNotificationsCollection = (id: EventCollectionId) =>
  buildCollection<EventNotification>({
    name: "Notifications",
    singularName: "Notification",
    description:
      "General notifications sent for this session (participants see this feed). Use Send to pick an account template.",
    path: buildEventNotificationsPath(id),
    id: "notifications",
    group: NAV_GROUP_SESSION,
    icon: "MarkEmailRead",
    initialSort: ["sentAt", "desc"],
    permissions: ({ authController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const allowed = canAdminEvent(id, extra);
      return {
        read: allowed,
        edit: false,
        create: false,
        delete: false,
      };
    },
    Actions: () => [
      <SendGeneralNotificationButton key="send-general-notification" />,
    ],
    properties: buildProperties<EventNotification>({
      type: buildProperty({
        name: "Type",
        dataType: "string",
        readOnly: true,
        enumValues: generalNotificationTypeEnum,
      }),
      title: buildLocalizedStringProperty({
        name: "Title",
        dataType: "string",
        readOnly: true,
      }),
      body: buildLocalizedStringProperty({
        name: "Body",
        multiline: true,
        dataType: "string",
        readOnly: true,
      }),
      sentAt: buildProperty({
        name: "Sent at",
        dataType: "date",
        mode: "date_time",
        readOnly: true,
      }),
      fcmSentAt: buildProperty({
        name: "Push sent at",
        dataType: "date",
        mode: "date_time",
        readOnly: true,
      }),
    }),
  });

const scopedNotificationProperties = buildProperties<ScopedNotification>({
  type: buildProperty({
    name: "Type",
    dataType: "string",
    readOnly: true,
    enumValues: NOTIFICATION_TYPE_LABELS,
  }),
  title: buildLocalizedStringProperty({
    name: "Title",
    dataType: "string",
    readOnly: true,
  }),
  body: buildLocalizedStringProperty({
    name: "Body",
    multiline: true,
    dataType: "string",
    readOnly: true,
  }),
  actionAt: buildProperty({
    name: "Action at",
    dataType: "date",
    mode: "date_time",
    readOnly: true,
  }),
  createdAt: buildProperty({
    name: "Created at",
    dataType: "date",
    mode: "date_time",
    readOnly: true,
  }),
});

export const buildParticipantNotificationsCollection = (
  _id: EventCollectionId
) =>
  buildCollection<ScopedNotification>({
    name: "Notifications",
    singularName: "Notification",
    description:
      "Personal notifications generated from this participant’s action logs (operator audit).",
    path: "notifications",
    id: "participant_notifications",
    icon: "Notifications",
    group: NAV_GROUP_SESSION,
    initialSort: ["createdAt", "desc"],
    permissions: () => ({
      read: true,
      edit: false,
      create: false,
      delete: false,
    }),
    properties: scopedNotificationProperties,
  });

export const buildTeamNotificationsCollection = (_id: EventCollectionId) =>
  buildCollection<ScopedNotification>({
    name: "Notifications",
    singularName: "Notification",
    description:
      "Team notifications generated from members’ action logs (operator audit).",
    path: "notifications",
    id: "team_notifications",
    icon: "Notifications",
    group: NAV_GROUP_SESSION,
    initialSort: ["createdAt", "desc"],
    permissions: () => ({
      read: true,
      edit: false,
      create: false,
      delete: false,
    }),
    properties: scopedNotificationProperties,
  });
